/**
 * Ce qu'une page de commune ou de période dit d'elle-même.
 *
 * Titres, introductions et fil d'Ariane vivent ici plutôt que dans le
 * composant, pour une raison qui a déjà servi : le pré-rendu au build a besoin
 * des mêmes textes pour remplir le `<head>`, et deux rédactions parallèles
 * finiraient par se contredire — la page dirait une chose, sa balise une autre.
 */

import {
  GENRE_DU_TYPE,
  NOM_DU_TYPE,
  ORGANISATEUR,
  commune,
  typeDe,
  type StagePublic,
} from './stage';
import {
  communesAvecStages,
  grouperParType,
  periodesAvecStages,
  type Regroupement,
} from './catalogue';

export type Genre = 'commune' | 'vacances';

export const BASE: Record<Genre, string> = {
  commune: '/stages/commune',
  vacances: '/stages/vacances',
};

/** Les regroupements d'un genre donné — ce qui aura une page. */
export function regroupements(genre: Genre, stages: StagePublic[]): Regroupement[] {
  return genre === 'commune' ? communesAvecStages(stages) : periodesAvecStages(stages);
}

/**
 * Les types que la page porte réellement, dans l'ordre des sections.
 *
 * L'ordre vient de `grouperParType`, qui range déjà les sections de la page :
 * dédoublonner soi-même donnait l'ordre des données, si bien qu'une page
 * annonçait « stages à thème et plaines de vacances » au-dessus de sections
 * rangées dans l'autre sens — et que deux communes n'employaient pas la même
 * phrase pour dire la même chose.
 */
function typesDe(stages: StagePublic[]): string[] {
  return grouperParType(stages).map((g) => g.titre.toLowerCase());
}

/**
 * Ce que la page annonce : les types présents, en toutes lettres.
 *
 * « des plaines de vacances et des stages à thème » plutôt qu'un décompte :
 * ce sont ces mots-là qu'une famille tape, et une phrase qui les contient
 * répond là où « 14 activités » ne répond à rien.
 */
function typesPresents(stages: StagePublic[]): string {
  const noms = typesDe(stages);
  if (noms.length === 0) return 'des activités de vacances';
  if (noms.length === 1) return `des ${noms[0]}`;
  return `des ${noms.slice(0, -1).join(', des ')} et des ${noms[noms.length - 1]}`;
}

/** La même énumération sans article, pour tenir après un deux-points. */
function enumererLesTypes(stages: StagePublic[]): string {
  const noms = typesDe(stages);
  if (noms.length === 0) return 'activités de vacances';
  if (noms.length === 1) return noms[0];
  return `${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`;
}

/**
 * L'accord d'un participe qui porte sur cette énumération.
 *
 * En français le masculin l'emporte dès qu'il est présent : « plaines de
 * vacances et stages à thème organisés », mais « plaines de vacances
 * organisées ». La règle est ici plutôt que dans la phrase, parce qu'elle vaut
 * pour tous les participes qu'on y ajoutera.
 */
function accordDesTypes(stages: StagePublic[]): 's' | 'es' {
  return stages.some((s) => GENRE_DU_TYPE[typeDe(s)] === 'm') ? 's' : 'es';
}

/**
 * « d'automne 2026 », « de printemps 2027 » — le complément de « les vacances ».
 *
 * Trois choses que la concaténation naïve rate, et que la production affichait :
 * l'élision devant voyelle ou h muet (« de Automne » était écrit tel quel), la
 * minuscule des noms de saison, et le fait que le libellé est modifiable depuis
 * l'écran d'administration alors que le code, lui, ne l'est pas.
 *
 * D'où la table, tenue sur les codes. Un code inconnu — quelqu'un ajoutera
 * peut-être « toussaint » ou « noel » un jour — retombe sur le libellé tel
 * qu'il est écrit, capitale comprise : c'est justement pour un nom propre
 * qu'on sort du vocabulaire des saisons, et le mettre en minuscules serait
 * l'erreur inverse.
 */
const VACANCES: Record<string, string> = {
  automne: 'd’automne',
  detente: 'de détente',
  ete: 'd’été',
  hiver: 'd’hiver',
  printemps: 'de printemps',
};

export function vacancesDe(r: Regroupement): string {
  const s = r.stages[0];
  if (!s) return r.nom;
  const connu = VACANCES[s.periode_code ?? ''];
  if (connu) return `${connu} ${s.annee ?? ''}`.trim();
  const libelle = s.periode ?? s.periode_code ?? r.nom;
  const elide = /^[aàâeéèêiîoôuùûyh]/i.test(libelle);
  return `${elide ? 'd’' : 'de '}${libelle} ${s.annee ?? ''}`.trim();
}

export function titreDeLaPage(genre: Genre, r: Regroupement): string {
  return genre === 'commune'
    ? `Stages et plaines de vacances pour enfants à ${r.nom}`
    : `Stages et plaines de vacances pour enfants à Bruxelles — ${r.nom}`;
}

/** Le `<title>`, plus court que le H1 : une balise trop longue est tronquée. */
export function baliseTitre(genre: Genre, r: Regroupement): string {
  return genre === 'commune'
    ? `Stages et plaines de vacances à ${r.nom} | Éclosion`
    : `Stages et plaines de vacances à Bruxelles — ${r.nom} | Éclosion`;
}

export function introductionDeLaPage(genre: Genre, r: Regroupement): string {
  const quoi = typesPresents(r.stages);
  if (genre === 'commune') {
    const centres = [...new Set(r.stages.map((s) => s.centre))];
    const ou =
      centres.length === 1
        ? `au centre ${centres[0]}`
        : `dans ${centres.length} centres : ${centres.join(', ')}`;
    return `${ORGANISATEUR} organise ${quoi} pour enfants à ${r.nom}, ${ou}. Chaque activité indique ses dates, la tranche d’âge accueillie et ses tarifs.`;
  }
  const communes = [...new Set(r.stages.map((s) => commune(s)))];
  const p = communes.length > 1 ? 's' : '';
  return `${ORGANISATEUR} organise ${quoi} pendant les vacances ${vacancesDe(r)}, dans ${communes.length} commune${p} bruxelloise${p} : ${communes.join(', ')}.`;
}

export function descriptionDeLaPage(genre: Genre, r: Regroupement): string {
  const n = r.stages.length;
  const ou = genre === 'commune' ? `à ${r.nom}` : `à Bruxelles pendant les vacances ${vacancesDe(r)}`;
  const details = genre === 'commune' ? 'Dates, âges, tarifs' : 'Dates, lieux, âges, tarifs';

  /*
    Une seule activité se décrit au singulier, avec son type à lui : « Une
    plaine de vacances à Forest ». La tournure plurielle annonçait jusqu'ici
    « plaines de vacances et stages à thème » même quand la page n'en portait
    qu'un seul, des deux — une promesse que la page ne tenait pas.
  */
  if (n === 1) {
    const t = typeDe(r.stages[0]);
    const feminin = GENRE_DU_TYPE[t] === 'f';
    return `${feminin ? 'Une' : 'Un'} ${NOM_DU_TYPE[t]} pour enfants ${ou}, organisé${feminin ? 'e' : ''} par ${ORGANISATEUR}. ${details} et inscription en ligne.`;
  }
  return `${n} activités pour enfants ${ou} : ${enumererLesTypes(r.stages)} organisé${accordDesTypes(r.stages)} par ${ORGANISATEUR}. ${details} et inscription en ligne.`;
}

export function filDArianeDeLaPage(): { nom: string; chemin: string }[] {
  return [
    { nom: 'Accueil', chemin: '/' },
    { nom: 'Stages', chemin: '/stages' },
  ];
}

export function titreDesVoisines(genre: Genre): string {
  return genre === 'commune'
    ? 'Nos activités dans les autres communes'
    : 'Les autres périodes de vacances';
}
